/**
 * Facebook Pixel + Conversions API event deduplication utility.
 *
 * Strategy:
 * 1. On app mount, fetch active pixels from /api/pixels/active and install the Meta Pixel base code.
 * 2. fbqTrack(eventName, params, eventId) calls window.fbq for all loaded pixels with a shared eventID.
 * 3. The same eventID is also used by the backend Conversions API so Meta deduplicates events.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _initialized = false;
let _loadedPixels = [];

function installFbqBase() {
  if (window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

export async function initFacebookPixels() {
  if (_initialized) return _loadedPixels;
  _initialized = true;
  try {
    const res = await axios.get(`${API}/pixels/active`);
    const pixels = res.data?.pixels || [];
    if (pixels.length === 0) return [];
    installFbqBase();
    _loadedPixels = pixels;
    pixels.forEach((p) => {
      try {
        window.fbq('init', String(p.id));
      } catch (e) {
        /* noop */
      }
    });
    // Initial PageView for all pixels
    try {
      window.fbq('track', 'PageView', {}, { eventID: `pv_${Date.now()}` });
    } catch (e) {
      /* noop */
    }
    return pixels;
  } catch (e) {
    return [];
  }
}

export function getLoadedPixels() {
  return _loadedPixels;
}

function genEventId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Standard Meta event track with deduplication eventID. */
export function fbqTrack(eventName, params = {}, customEventId = null) {
  if (!window.fbq) return null;
  const eventID = customEventId || genEventId(eventName.toLowerCase());
  try {
    window.fbq('track', eventName, params, { eventID });
  } catch (e) {
    /* noop */
  }
  return eventID;
}

/** Custom (non-standard) event. */
export function fbqTrackCustom(eventName, params = {}, customEventId = null) {
  if (!window.fbq) return null;
  const eventID = customEventId || genEventId(eventName.toLowerCase());
  try {
    window.fbq('trackCustom', eventName, params, { eventID });
  } catch (e) {
    /* noop */
  }
  return eventID;
}

/** Fire PageView on SPA route change. */
export function trackPageView(path) {
  fbqTrack('PageView', { path: path || window.location.pathname });
}

/** Standard events with the Visyonn product format. */
export function pixelViewContent(product) {
  if (!product) return;
  fbqTrack('ViewContent', {
    content_ids: [product.product_id],
    content_name: product.name,
    content_type: 'product',
    content_category: product.subcategory || product.category || '',
    value: Number(product.price || 0),
    currency: 'BRL',
  });
}

export function pixelAddToCart(product, size, color, quantity = 1) {
  if (!product) return;
  fbqTrack('AddToCart', {
    content_ids: [product.product_id],
    content_name: product.name,
    content_type: 'product',
    contents: [{ id: product.product_id, quantity: Number(quantity) || 1 }],
    value: Number(product.price || 0) * (Number(quantity) || 1),
    currency: 'BRL',
    extra_size: size,
    extra_color: color,
  });
}

export function pixelInitiateCheckout(items, total) {
  fbqTrack('InitiateCheckout', {
    content_ids: items.map((i) => i.product_id),
    content_type: 'product',
    contents: items.map((i) => ({ id: i.product_id, quantity: Number(i.quantity) || 1 })),
    num_items: items.reduce((s, i) => s + (Number(i.quantity) || 1), 0),
    value: Number(total || 0),
    currency: 'BRL',
  });
}

export function pixelPurchase(orderId, items, total) {
  // Must match backend event_id for deduplication: backend uses `purchase_{order_id}`
  fbqTrack(
    'Purchase',
    {
      content_ids: items.map((i) => i.product_id),
      content_type: 'product',
      contents: items.map((i) => ({ id: i.product_id, quantity: Number(i.quantity) || 1, item_price: Number(i.price || 0) })),
      num_items: items.reduce((s, i) => s + (Number(i.quantity) || 1), 0),
      value: Number(total || 0),
      currency: 'BRL',
      order_id: orderId,
    },
    `purchase_${orderId}`,
  );
}

export function pixelSearch(query) {
  fbqTrack('Search', { search_string: String(query || '') });
}

export function pixelCompleteRegistration() {
  fbqTrack('CompleteRegistration');
}

export function pixelLead() {
  fbqTrack('Lead');
}
